'use client';

import { motion } from 'framer-motion';
import { entrance } from '@/src/shared/lib/chart/entrance';
import { usePrefersReducedMotion } from '@/src/shared/lib/usePrefersReducedMotion';

const SEGMENT_STAGGER = 0.08;

type Segment = { key: string; pct: number; color: string };

type StackedBarProps = { segments: Segment[]; ariaLabel: string; height?: number };

/** Shares of a whole side by side, each growing in after the one before it. */
export function StackedBar({ segments, ariaLabel, height = 10 }: StackedBarProps) {
  const reduced = usePrefersReducedMotion();
  return (
    <div role="img" aria-label={ariaLabel} className="flex w-full gap-0.5" style={{ height }}>
      {segments
        .filter((s) => s.pct > 0)
        .map((s, i) => (
          <motion.span
            key={s.key}
            data-segment={s.key}
            className="h-full rounded-[3px]"
            style={{ width: `${s.pct}%`, background: s.color, transformOrigin: 'left' }}
            {...entrance('growX', { reduced, delay: i * SEGMENT_STAGGER })}
          />
        ))}
    </div>
  );
}

type MeterBarProps = { pct: number; color: string; ariaLabel: string; height?: number };

/** How full something is, on a muted track. */
export function MeterBar({ pct, color, ariaLabel, height = 6 }: MeterBarProps) {
  const reduced = usePrefersReducedMotion();
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className="w-full overflow-hidden rounded-full bg-muted"
      style={{ height }}
    >
      <motion.span
        className="block h-full rounded-full"
        style={{
          width: `${width}%`,
          background: `linear-gradient(90deg, color-mix(in oklab, ${color} 55%, transparent), ${color})`,
          transformOrigin: 'left',
        }}
        {...entrance('growX', { reduced })}
      />
    </div>
  );
}
